import { datadogRum } from "@datadog/browser-rum";
import React, { ReactNode, useEffect, useState } from "react";
import { AuthenticationToken, ProspectUserDetails } from "../types/Auth";
import { getLocalStorageItem } from "best-common-react";
import { LocalStorageConstants } from "../constants/LocalStorageConstants";
import { removeLocalStorageItem } from "best-common-react/lib/esm/utils/LocalStorageUtil";
import { getProspectUserDetails, AuthenticatedClient } from "../api/AuthenticatedClient";
import { persistToken, refresh } from "../api/auth";

type AuthenticationContextType = {
  userDetails?: ProspectUserDetails;
  refreshUserDetails?: () => Promise<void>;
  authenticated: boolean;
  setAuthenticated: (value: boolean) => void;
  setRegistrationStatusId: (value: number) => void;
  getToken: () => AuthenticationToken;
  logout: () => void;
};

const AuthenticationContext = React.createContext<AuthenticationContextType | undefined>(undefined);

type AuthenticationProviderProps = {
  children?: ReactNode;
};

const AuthenticationProvider: React.FC<AuthenticationProviderProps> = ({ ...props }) => {
  const [authenticated, setAuthenticated] = useState<boolean>();
  const [userDetails, setUserDetails] = useState<ProspectUserDetails>();

  const getToken = (): AuthenticationToken => {
    return getLocalStorageItem<AuthenticationToken>(LocalStorageConstants.AUTH0_TOKEN);
  };

  const logout = () => {
    removeLocalStorageItem(LocalStorageConstants.AUTH0_TOKEN);
    setAuthenticated(false);
  };

  // Configure the client to use Bearer token when authenticated
  AuthenticatedClient.interceptors.request.use((config) => {
    const token = getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token.accessToken}`;
      config.headers["x-id-token"] = token.idToken;
    }

    return config;
  });

  AuthenticatedClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 || error.response?.status === 403) {
        // Refresh token and retry request
        if (!originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const token = getToken();
            const res = await refresh(token.refreshToken);
            persistToken(res);

            return AuthenticatedClient(originalRequest);
          } catch (refreshError) {
            console.error(refreshError);
          }
        }

        // Logout if retry is unsuccessful
        logout();
      }

      return Promise.reject(error);
    },
  );

  useEffect(() => {
    const token = getToken();
    setAuthenticated(!!token);
  }, []);

  useEffect(() => {
    if (!!userDetails) {
      datadogRum.setUser({
        id: `${userDetails.prospectId}`,
        email: userDetails.username,
        name: `${userDetails.lastName}, ${userDetails.firstName}`,
      });
    } else {
      datadogRum.removeUser();
    }
  }, [userDetails]);

  // Fetch prospect's UserDetails record if authenticated - will cause logout if token is invalid
  useEffect(() => {
    if (authenticated) {
      fetchProspectUserDetails().catch(console.error);
    }
  }, [authenticated]);

  const fetchProspectUserDetails = async () => {
    const res = await getProspectUserDetails();
    setUserDetails(res);
  };

  const setRegistrationStatusId = (value: number) => {
    if (userDetails) {
      setUserDetails({ ...userDetails, registrationStatusId: value });
    }
  };

  return (
    <AuthenticationContext.Provider
      value={{
        authenticated,
        userDetails,
        refreshUserDetails: fetchProspectUserDetails,
        setAuthenticated,
        setRegistrationStatusId,
        getToken,
        logout,
      }}
      {...props}
    />
  );
};

const useAuthentication = (): AuthenticationContextType => {
  const context: AuthenticationContextType | undefined = React.useContext(AuthenticationContext);
  if (context === undefined) {
    throw new Error(`useAuthentication must be used within a AuthenticationProvider`);
  }
  return context;
};

export { AuthenticationProvider, useAuthentication };
